import type { CombinedError, Operation } from 'urql';

import type { Ory } from '@revelio/auth';
import {
  getUrqlErrorType,
  isWarningError,
  KIBANA_LOG_LEVEL,
  type LogBody,
} from '@revelio/data-access';

type LogOptions = Omit<LogBody, 'message' | 'level' | 'source'>;

// The x-request-id urql sets on each operation — the correlation key between
// what a user sees, the error log, and the backend trace.
export const getOperationRequestId = (
  operation: Operation
): string | undefined => {
  const fetchOptions = operation?.context?.fetchOptions;
  const headers = ((): { [key: string]: string } | undefined => {
    if (fetchOptions instanceof Function) {
      return fetchOptions()?.headers as { [key: string]: string } | undefined;
    }
    return fetchOptions?.headers as { [key: string]: string } | undefined;
  })();

  return headers?.['x-request-id'];
};

export class KibanaLogger {
  private source;
  public getAuthInfo;
  private dashMetaRoot;
  public sendLogs;
  private version?: string;
  private getSessionUrl?: () => string | undefined;

  constructor({
    source = 'REVELIO_DASHBOARD',
    getAuthInfo,
    dashMetaRoot,
    sendLogs = true,
    version,
    getSessionUrl,
  }: {
    source: string;
    getAuthInfo: () => {
      dashMetaCsrfToken?: string;
      ory?: Partial<Ory>;
    };
    dashMetaRoot: string;
    sendLogs?: boolean;
    version?: string;
    /** Session-replay deep link supplier (e.g. OpenReplay getSessionURL). */
    getSessionUrl?: () => string | undefined;
  }) {
    this.source = source;
    this.getAuthInfo = getAuthInfo;
    this.dashMetaRoot = dashMetaRoot;
    this.sendLogs = sendLogs;
    this.version = version;
    this.getSessionUrl = getSessionUrl;
  }

  public setSendLogs(status: boolean) {
    this.sendLogs = status;
  }

  public log = async (message: string, logOptions?: LogOptions) => {
    const logBody: LogBody = {
      source: this.source,
      level: KIBANA_LOG_LEVEL.INFO,
      message,
      version: this.version,
      username: this.getAuthInfo()?.ory?.username,
      'request-id': crypto.randomUUID(),
      ...logOptions,
    };
    return this.sendLog(logBody);
  };

  public warn = async (message: string, logOptions?: LogOptions) => {
    const logBody: LogBody = {
      source: this.source,
      level: KIBANA_LOG_LEVEL.WARN,
      message,
      version: this.version,
      username: this.getAuthInfo()?.ory?.username,
      'request-id': crypto.randomUUID(),
      ...logOptions,
    };

    return this.sendLog(logBody);
  };

  public error = async (message: string, logOptions?: LogOptions) => {
    const logBody: LogBody = {
      source: this.source,
      level: KIBANA_LOG_LEVEL.ERROR,
      message,
      version: this.version,
      username: this.getAuthInfo()?.ory?.username,
      'request-id': crypto.randomUUID(),
      ...logOptions,
    };
    return this.sendLog(logBody);
  };

  private sendLog = async (logBody: LogBody) => {
    if (!this.sendLogs) {
      console.warn('Sending logs is turned off');
      return { message: 'Sending logs is turned off' };
    }

    const csrfToken = this.getAuthInfo()?.dashMetaCsrfToken;
    if (!csrfToken) {
      console.warn('Missing CSRF token');
      return { message: 'Missing CSRF token' };
    }

    const body: LogBody = {
      page:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
      // href keeps the query string (lz-string filters state), so the link
      // restores the exact app state that produced the log
      page_url:
        typeof window !== 'undefined' ? window.location.href : undefined,
      session_url: this.safeSessionUrl(),
      ...logBody,
    };

    const sendLogResponse = await fetch(`${this.dashMetaRoot}/api/log`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken,
      },
      body: JSON.stringify(body),
    });
    return await sendLogResponse.json();
  };

  private safeSessionUrl = (): string | undefined => {
    try {
      return this.getSessionUrl?.();
    } catch {
      return undefined;
    }
  };

  public getUrqlOperationLog = (operation: Operation) => {
    const requestId = getOperationRequestId(operation);

    const oryUser = this.getAuthInfo()?.ory;
    const username = oryUser?.username || '';

    const query = operation.query?.loc?.source?.body
      ?.replace(/\n/g, '')
      ?.replace(/\s\s+/g, ' ');
    const variables = JSON.stringify(operation.variables);

    const message = `query: ${query}\n\nvariables:${variables}`;

    const logOptions: LogOptions = {
      'request-id': requestId,
      url: operation?.context?.url,
      username,
    };

    return { message, logOptions };
  };

  public getUrqlErrorLog = (error: CombinedError, operation: Operation) => {
    const { message, logOptions } = this.getUrqlOperationLog(operation);

    const response = error?.response as Response | undefined;
    const status = response?.status;

    const responseHeaders = response?.headers;
    const correlationId = responseHeaders?.get('X-Correlation-Id') || undefined;

    const errorMessage = `[${status}] ${error.message}\n\n${message}`;

    const errorLogOptions: LogOptions = {
      ...logOptions,
      'correlation-id': correlationId,
    };

    return {
      message: errorMessage,
      logOptions: errorLogOptions,
      isWarningLevel: isWarningError(error),
      errorType: getUrqlErrorType(error),
    };
  };
}
